import Vue from 'vue'
import store from '@/store'
import GeolocationManager from '../../geolocationManager'
import config from '@/config'
import utils from '../../common/utils'
import lexisNexisInit from '../../../sdkScripts/lexisnexis'

const ERROR_MSG_CODE = ['ERROR_VERIFY_APP']
const ERROR_MSG_DESCRIPTION = {
  'ERROR_VERIFY_APP': 'The Radar Verify app is not installed or running.'
}
const isDebug = sessionStorage.geolocationDebug
const GEOLOCATION_CONFIG = config.app.autoconf.GEOLOCATION
const INTERVAL_SECONDS = 1200
let geoManager = null
let userID = null
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

async function startGeolocation (user, sessionId, reason = 'Login') {
  let isLogin = false
  if (reason === 'Login') {
    isLogin = 'Login'
    localStorage.geolocationOnLogin = true
    utils.showSpinnerOnStart()
  }
  if (reason === 'IP changed') stopPolling(false)
  let init = await lexisNexisInit(isLogin)
  if (!init.valid) return false
  userID = user
  sessionId = window.webSessionId
  if (window.threatmetrix && window.threatmetrix.Geofence && !window.profileConnected) {
    /* Connect threatmetrix service and prepare for geolocation request. */
    window.threatmetrix.Geofence.init({apiKey: GEOLOCATION_CONFIG.LEXISNEXIS.API_KEY, cb})
    softCheck(user, sessionId, reason)
  }
  if (window.threatmetrix && window.threatmetrix.Geofence && window.profileConnected) {
    softCheck(user, sessionId, reason)
  }
}

/** --------------------------------
 * Threatmetrix callback method - cb is mandatory method name.
 -------------------------------- **/
function cb (token) {
  if (isDebug) console.log('[LexisNexis] Callback response:', token)
  if (token && token.token) {
    let data = token.token
    if (token && token.user && !token.user.userId) return false
    managerInstance.geolocationManager.validateGeolocation(data, userID)
  }
}

/** --------------------------------
 *  Start polling request and listen cb response.
 -------------------------------- **/
function startPolling (user, sessionId, reason) {
  sessionId = window.webSessionId
  if (isDebug) console.log('[LexisNexis] Getting location...', {user, sessionId, reason})
  window.threatmetrix.Geofence.startPolling(INTERVAL_SECONDS, sessionId, user, false)
}

/** --------------------------------
 *  Stop polling request.
 -------------------------------- **/
function stopPolling (removeSessionId = true) {
  if (removeSessionId) {
    localStorage.removeItem('lnSessionId')
    window.webSessionId = undefined
  }
  if (window.threatmetrix && window.threatmetrix.Geofence) {
    if (isDebug) console.log('[LexisNexis] stop polling.')
    window.threatmetrix.Geofence.stopPolling()
  }
}

/** --------------------------------
* Soft check to see if the user has the Radar Verify app installed and running.
 -------------------------------- **/
async function softCheck (user, sessionId, reason) {
  await sleep(500)
  window.profileConnected = true
  try {
    const result = await window.threatmetrix.Geofence.softCheck()
    if (isDebug) console.log('[LexisNexis] Soft Check response:', result)
    startPolling(user, sessionId, reason)
  } catch (error) {
    const errorCode = error.status || error
    if (isDebug) console.log('[LexisNexis] Soft Check exception:', errorCode)
    errorHandler(errorCode, ERROR_MSG_DESCRIPTION[errorCode], user, reason)
  }
}

function continueGeolocationProcess () {
  let userID = localStorage.customer
  let sessionId = window.webSessionId
  // store.dispatch('initGeolocAttempt')
  startGeolocation(userID, sessionId, 'Reload')
}

function validateResponse (result, userID) {
  store.commit('setOnLoginGeolocationProgress', 'validate_geo_response')
  sessionStorage.removeItem('tempPromotionSection')
  if (result.IsValid) {
    locationVerified(result, userID)
  }
  if (!result.IsValid) {
    locationFailed(result, userID)
    store.commit('setGeocomplyTroubleshooter', result.Troubleshooter)
  }
}

/** --------------------------------
 *  location verified ; allow user to place bet, remove error messages, prepare periodic location check
 -------------------------------- **/
function locationVerified () {
  store.commit('setOnLoginGeolocationProgress', 'completed')
  if (isDebug) console.log('[LexisNexis] Location is verified.')
  localStorage.removeItem('tgc')
  localStorage.setItem('gcPerm', true)
  store.commit('setGeolocated', true)
  setTimeout(() => {
    if (localStorage.geolocationOnLogin) {
      store.dispatch('triggerBSLocationVerifiedAnimation', true)
      localStorage.removeItem('geolocationOnLogin')
    }
  }, 100)
  localStorage.setItem('geoError', false)
  store.commit('overlayState/setGeolocationMessage', {})
  setTimeout(() => {
    store.commit('setGeolocationOnLogin', false)
  }, 6000)
}

/** --------------------------------
 * location failed ; remove permission, display errors
 -------------------------------- **/
function locationFailed (response, userID) {
  if (isDebug) console.log('[LexisNexis] Location not verified.', response.Troubleshooter)
  store.commit('setOnLoginGeolocationProgress', 'completed')
  localStorage.removeItem('geolocationOnLogin')
  localStorage.setItem('gcMessages', JSON.stringify(response.Troubleshooter))
  let errorCode = 999
  stopPolling()
  displayErrorMessage(errorCode, response.Troubleshooter, userID)
  store.commit('setGeolocationOnLogin', false)
}

function errorHandler (errorCode, errorMessage, userID, reason) {
  geocomplyTechnicalError(errorCode, errorMessage, userID, reason)
  var isCode = ERROR_MSG_CODE.includes(errorCode)
  if (isCode) {
    store.commit('setGeolocated', false)
    localStorage.setItem('gcPerm', false)
    if (ERROR_MSG_CODE.includes(612) || ERROR_MSG_CODE.includes('ERROR_VERIFY_APP')) {
      errorCode = 612
      localStorage.setItem('geoError', 612)
    }
    displayErrorMessage(errorCode, errorMessage, userID)
  }
}

/** --------------------------------
 *  user is not geolocated or plugin not exist ; provide message to the user
 -------------------------------- **/
function displayErrorMessage (errorCode, errorMessage, userID) {
  if (isDebug) console.log('[LexisNexis] location / plugin error message.')
  localStorage.setItem('geoError', errorCode)
  store.commit('overlayState/setGeolocationMessage', { errorCode, errorMessage, userID })
  store.dispatch('overlayState/activeGeolocationDialog')
}

function geocomplyTechnicalError (errorCode, errorMessage, userID, reason) {
  let keyName = errorCode
  let logReason = 'Desktop: Sports/ ' + reason
  let attemptID = store.getters['getGeolocationAttempt']
  store.dispatch('completeGeolocAttempt', {errorCode, errorMessage, userID, logReason, keyName, attemptID})
}

function downloadPlugin () {
  const pluginURL = GEOLOCATION_CONFIG.LEXISNEXIS.PLUGIN_URL
  if (!pluginURL) return

  const appVer = navigator.appVersion || ''
  const isWindows = appVer.includes('Win')
  const isMac = appVer.includes('Mac')
  const navigatorID = isWindows ? 'windows' : isMac ? 'mac' : ''
  const link = navigatorID ? `${pluginURL}/${navigatorID}/latest` : pluginURL
  if (isDebug) console.log('[LexisNexis] Downloading geolocation plugin...', link)
  window.location = link
}

function disconnectService () {
  stopPolling()
}

const managerInstance = {
  get geolocationManager () {
    if (!geoManager) geoManager = GeolocationManager.getInstance(config.app.autoconf.GEOLOCATION.PROVIDER, Vue.prototype.$geolocationDebug)
    return geoManager
  }
}

export default {
  startGeolocation,
  downloadPlugin,
  validateResponse,
  softCheck,
  disconnectService,
  continueGeolocationProcess
}



// WEBPACK FOOTER //
// ./src/library/geolocation/services/providers/lexisNexis/helpers.js